// Penalty Takers — per-team penalty conversion bars (Phase 3).
//
// Emits the SAME DOM + class names as the legacy renderPenaltyTakersModule, so
// the shared CSS renders it identically (verified by the visual A/B test). Data
// comes from app-api via `useWidgetData`; the team control stays a
// legacy shell select and its value arrives through the bridge context (falling
// back to the fixture's home team, matching the legacy `teamVal || t1`).
//
// The legacy renderer adds `pt-body` to the *module body* (its own container) to
// left-align the stack (`.module-body.pt-body { justify-content: flex-start }`).
// Here the React root IS that body, so a layout effect tags the host element and
// removes it on unmount. In Phase 4 (when the shell is React) this becomes a
// className on the body JSX.
//
// The `.sc-bar` has no CSS width transition — the legacy sets the final width in
// a rAF (a paint tick, not an animation) — so we render it at its final width.

import { useLayoutEffect, useRef } from "react";
import { shapePenaltyTakers } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface PenaltyTakersProps {
  fixture: string;
  /** Selected team (full name); falls back to the fixture's home team. */
  team?: string | null;
}

export default function PenaltyTakers({ fixture, team }: PenaltyTakersProps) {
  const home = (fixture || "Arsenal v Everton").split(" v ")[0]?.trim() ?? "";
  const selected = team || home;
  const {
    data: takers,
    isPending,
    isError,
    refetch,
  } = useWidgetData("penalty-takers", useFixtureKey(fixture));
  const players = shapePenaltyTakers(takers?.[selected] ?? []);

  // Tag the host module body so the shared CSS left-aligns the stack. Depends on
  // the fetch state: while pending/error the host isn't rendered (hostRef is
  // null), so re-run once the loaded/empty wrapper mounts to add the class then.
  const hostRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const body = hostRef.current?.parentElement;
    if (!body) return;
    body.classList.add("pt-body");
    return () => body.classList.remove("pt-body");
  }, [isPending, isError]);

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  if (!players.length) {
    return (
      <div className="pt-empty" ref={hostRef}>
        No penalty data recorded
      </div>
    );
  }

  return (
    <div className="pt-wrapper" data-pt-team={selected} ref={hostRef}>
      {players.map((p, i) => (
        <div className="sc-block" key={`${p.name}-${i}`}>
          <div className="sc-team-label">{p.name}</div>
          <div className="sc-stat">
            <div className="sc-stat-header">
              <span className="sc-stat-name">
                {p.scored}/{p.taken}
              </span>
              <span className="sc-stat-val">{p.pct}%</span>
            </div>
            <div className="sc-track">
              <div
                className="sc-bar sc-scored bento-fill"
                data-pct={p.pct}
                style={{ width: `${p.pct}%` }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
